/** HUD copy in English and Turkish. */

export type Language = "en" | "tr";

export const LANGUAGES: readonly Language[] = ["en", "tr"];

export const LANGUAGE_LABELS: Readonly<Record<Language, string>> =
  Object.freeze({
    en: "EN",
    tr: "TR",
  });

const EN = {
  "controls.group": "Game controls",
  "controls.pause": "Pause",
  "controls.resume": "Resume",
  "controls.mute": "Mute game sounds",
  "controls.unmute": "Enable game sounds",
  "controls.rotateLeft": "Rotate view 10 degrees left",
  "controls.rotateRight": "Rotate view 10 degrees right",
  "controls.zoomIn": "Zoom in",
  "controls.zoomOut": "Zoom out",
  "controls.scores": "Scores",
  "controls.settings": "Settings",
  "controls.hideStats": "Hide scoreboard",
  "controls.showStats": "Show scoreboard",
  "controls.language": "Switch language",
  "controls.symbolsOn": "Show shapes instead of relying on colour",
  "controls.symbolsOff": "Hide the colour-blind shapes",

  "hint.drag": "Drag this plane",

  "stats.title": "Flight status",
  "stats.score": "Score",
  "stats.landings": "Landings",
  "stats.departures": "Departures",
  "stats.pace": "Pace",
  "stats.duration": "Duration",
  "stats.perMinute": "/min",

  "fps.title": "Frames per second",
  "fps.unit": "FPS",

  "a11y.selected": "{colour} aircraft selected",
  "a11y.noneSelected": "No aircraft selected",
  "symbol.yellow": "triangle",
  "symbol.blue": "circle",
  "symbol.red": "square",
  "colour.yellow": "Yellow",
  "colour.blue": "Blue",
  "colour.red": "Red",

  "pause.title": "Paused",
  "pause.resume": "Resume",
  "pause.restart": "Restart",

  "gameover.title": "Crash!",
  "gameover.subtitle": "Two aircraft lost separation.",
  "gameover.landings": "Landings",
  "gameover.departures": "Departures",
  "gameover.duration": "Duration",
  "gameover.again": "Fly again",
  "gameover.best": "Best: {landings} landings",
  "gameover.newBest": "New personal best!",
  "gameover.saving": "Saving your run…",
  "gameover.savedRank": "Saved — rank #{rank}",
  "gameover.saved": "Saved to the board",
  "gameover.unavailable": "Board temporarily unavailable",
  "gameover.namePlaceholder": "Your name",
  "gameover.nameSave": "Save name",
  "gameover.nameSaved": "Saved as {name}",
  "gameover.nameRefused": "That name is taken. Try another.",

  "board.title": "Top ten",
  "board.loading": "Loading the board…",
  "board.unavailable": "Board temporarily unavailable",
  "board.empty": "No runs on the board yet.",
  "board.anonymous": "Anonymous",

  "leaderboard.title": "Scores",
  "leaderboard.yourRuns": "Your last runs",
  "leaderboard.noRuns": "You have not finished a run yet.",
  "leaderboard.landingsShort": "L",
  "leaderboard.departuresShort": "D",
  "leaderboard.reload": "Refresh",

  "controls.volume": "Sound volume",

  "settings.title": "Settings",
  "settings.sound": "Game sounds",
  "settings.volume": "Volume",
  "settings.scoreboard": "Scoreboard",
  "settings.shapes": "Colour-blind shapes",
  "settings.fps": "Frame rate",
  "settings.language": "Language",
  "settings.on": "On",
  "settings.off": "Off",
  "settings.close": "Done",

  "tutorial.title": "How it works",
  "tutorial.colour":
    "Every aircraft lands only on the runway of its own colour.",
  "tutorial.drag":
    "Drag from an aircraft and finish on the matching runway's approach gate.",
  "tutorial.separation":
    "Two aircraft touching in the air ends the run. White rings warn you first.",
  "tutorial.symbols":
    "Colour blind? Settings has a ▲ ● ■ switch that marks every runway and aircraft with a shape.",
  "tutorial.dismiss": "Got it",

  "error.webgl":
    "This browser could not start WebGL, so the airport cannot be drawn.",
  "error.retry": "Retry",
} as const;

export type TranslationKey = keyof typeof EN;

const TR: Readonly<Record<TranslationKey, string>> = {
  "controls.group": "Oyun kontrolleri",
  "controls.pause": "Duraklat",
  "controls.resume": "Devam et",
  "controls.mute": "Sesi kapat",
  "controls.unmute": "Sesi aç",
  "controls.rotateLeft": "Görünümü 10 derece sola çevir",
  "controls.rotateRight": "Görünümü 10 derece sağa çevir",
  "controls.zoomIn": "Yakınlaştır",
  "controls.zoomOut": "Uzaklaştır",
  "controls.scores": "Skorlar",
  "controls.settings": "Ayarlar",
  "controls.hideStats": "Skor tablosunu gizle",
  "controls.showStats": "Skor tablosunu göster",
  "controls.language": "Dili değiştir",
  "controls.symbolsOn": "Renk yerine şekilleri göster",
  "controls.symbolsOff": "Renk körlüğü şekillerini gizle",

  "hint.drag": "Bu uçağı sürükle",

  "stats.title": "Uçuş durumu",
  "stats.score": "Puan",
  "stats.landings": "İniş",
  "stats.departures": "Kalkış",
  "stats.pace": "Tempo",
  "stats.duration": "Süre",
  "stats.perMinute": "/dk",

  "fps.title": "Saniyedeki kare sayısı",
  "fps.unit": "FPS",

  "a11y.selected": "{colour} uçak seçildi",
  "a11y.noneSelected": "Uçak seçili değil",
  "symbol.yellow": "üçgen",
  "symbol.blue": "daire",
  "symbol.red": "kare",
  "colour.yellow": "Sarı",
  "colour.blue": "Mavi",
  "colour.red": "Kırmızı",

  "pause.title": "Duraklatıldı",
  "pause.resume": "Devam et",
  "pause.restart": "Yeniden başlat",

  "gameover.title": "Çarpışma!",
  "gameover.subtitle": "İki uçak arasındaki mesafe kayboldu.",
  "gameover.landings": "İniş",
  "gameover.departures": "Kalkış",
  "gameover.duration": "Süre",
  "gameover.again": "Tekrar uç",
  "gameover.best": "En iyi: {landings} iniş",
  "gameover.newBest": "Yeni rekor!",
  "gameover.saving": "Turun kaydediliyor…",
  "gameover.savedRank": "Kaydedildi — sıra #{rank}",
  "gameover.saved": "Tabloya kaydedildi",
  "gameover.unavailable": "Tablo şu an kullanılamıyor",
  "gameover.namePlaceholder": "Adın",
  "gameover.nameSave": "Adı kaydet",
  "gameover.nameSaved": "{name} olarak kaydedildi",
  "gameover.nameRefused": "Bu ad kullanılamıyor. Başka bir ad dene.",

  "board.title": "İlk on",
  "board.loading": "Tablo yükleniyor…",
  "board.unavailable": "Tablo şu an kullanılamıyor",
  "board.empty": "Tabloda henüz tur yok.",
  "board.anonymous": "İsimsiz",

  "leaderboard.title": "Skorlar",
  "leaderboard.yourRuns": "Son turların",
  "leaderboard.noRuns": "Henüz bitirdiğin bir tur yok.",
  "leaderboard.landingsShort": "İ",
  "leaderboard.departuresShort": "K",
  "leaderboard.reload": "Yenile",

  "controls.volume": "Ses seviyesi",

  "settings.title": "Ayarlar",
  "settings.sound": "Oyun sesleri",
  "settings.volume": "Ses seviyesi",
  "settings.scoreboard": "Skor tablosu",
  "settings.shapes": "Renk körlüğü şekilleri",
  "settings.fps": "Kare hızı",
  "settings.language": "Dil",
  "settings.on": "Açık",
  "settings.off": "Kapalı",
  "settings.close": "Tamam",

  "tutorial.title": "Nasıl oynanır",
  "tutorial.colour": "Her uçak yalnızca kendi rengindeki piste iner.",
  "tutorial.drag":
    "Uçaktan sürükle ve aynı renkteki pistin yaklaşma kapısında bırak.",
  "tutorial.separation":
    "Havada birbirine değen iki uçak turu bitirir. Önce beyaz halka uyarır.",
  "tutorial.symbols":
    "Renk körlüğü mü? Ayarlardaki ▲ ● ■ anahtarı her piste ve uçağa bir şekil koyar.",
  "tutorial.dismiss": "Anladım",

  "error.webgl": "Bu tarayıcı WebGL başlatamadı, havaalanı çizilemiyor.",
  "error.retry": "Yeniden dene",
};

export const TRANSLATIONS: Readonly<
  Record<Language, Readonly<Record<TranslationKey, string>>>
> = Object.freeze({ en: EN, tr: TR });

export function isLanguage(value: unknown): value is Language {
  return value === "en" || value === "tr";
}

/**
 * Pick a language from a browser's preference list.
 *
 * Matches on the primary subtag, so "tr-TR" and "tr" both land on Turkish.
 */
export function detectLanguage(
  candidates: readonly string[] | undefined,
  fallback: Language = "en",
): Language {
  for (const candidate of candidates ?? []) {
    const primary = candidate.split("-")[0]?.toLowerCase();
    if (isLanguage(primary)) {
      return primary;
    }
  }
  return fallback;
}

export type Translate = (
  key: TranslationKey,
  params?: Readonly<Record<string, string | number>>,
) => string;

/** `{name}` placeholders are filled from `params`; unknown ones are left as is. */
export function translator(language: Language): Translate {
  const dictionary = TRANSLATIONS[language] ?? TRANSLATIONS.en;
  return (key, params) => {
    const template = dictionary[key] ?? TRANSLATIONS.en[key] ?? key;
    if (!params) {
      return template;
    }
    return template.replace(/\{(\w+)\}/gu, (match, name: string) =>
      name in params ? String(params[name]) : match,
    );
  };
}
